import React, { useState, useRef } from 'react';
import { useTransition, config } from '@react-spring/web';

import useBreakpoint from 'Common/hooks/useBreakpoint';
import { useTheme } from 'styled-components';

import AvatarMobile from './subcomponent/AvatarMobile/AvatarMobile';
import VisuallyHidden from 'Elements/VisuallyHidden';
import Image from 'next/image';
import defaultProfileImage from 'Public/images/cards/profile/default.png';

import {
    Tabs,
    Tab,
    Label,
    Panel,
    AvatarWrapper,
    AvatarContainer,
    BioContainer,
    Position,
    PanelWrapper,
    Organisation
} from './Avatar.styled';

export interface Tab {
    name?: string;
    avatar?: {
        src: string;
    };
    id: string;
    content: React.ReactNode | string;
    position: string;
    organisation?: string;
}

export interface Props {
    tabs: Tab[];
    id: string;
}

const Avatar: React.FC<Props> = ({ tabs, id }) => {
    //breakpoint hooks
    const { breakpoints } = useTheme();
    const isMediumBreakpoint = useBreakpoint(breakpoints.medium.value);
    //breakpoint hooks end

    //tab hooks
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [hoverTabIndex, setHoverTabIndex] = useState<null | number>(null);
    const tabsRef = useRef<HTMLDivElement>(null);
    // tab hooks end

    const animation = useTransition(tabs[activeTabIndex], {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: config.stiff
    });

    const handleTabKeydown = (event: React.KeyboardEvent) => {
        if (tabsRef.current && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            const tabsEl = tabsRef.current;
            let newIndex: number;

            if (event.key === 'ArrowLeft') {
                newIndex = activeTabIndex - 1 < 0 ? tabs.length - 1 : activeTabIndex - 1;
            } else {
                newIndex = activeTabIndex + 1 > tabs.length - 1 ? 0 : activeTabIndex + 1;
            }

            setActiveTabIndex(newIndex);
            (tabsEl.children[newIndex] as HTMLButtonElement).focus();
        }
    };

    const renderTab = (tab: Tab, index: number): React.ReactNode => {
        return (
            <Tab
                id={`tab-${tab.id}`}
                key={`tab-${tab.id}`}
                role="tab"
                aria-label={`Click for more information about ${tab.name}, ${tab.position}`}
                aria-controls={`panel-${tab.id}`}
                aria-selected={index === activeTabIndex}
                onClick={() => setActiveTabIndex(index)}
                onKeyDown={handleTabKeydown}
                onMouseOver={() => setHoverTabIndex(index)}
                onMouseOut={() => setHoverTabIndex(null)}
                data-hover={hoverTabIndex === index}
                $isActive={index === activeTabIndex}
            >
                <AvatarContainer>
                    <AvatarWrapper>
                        <Image
                            src={tab.avatar?.src || defaultProfileImage}
                            alt=""
                            fill
                            style={{ objectFit: 'cover' }}
                        />
                    </AvatarWrapper>
                    {tab.name && <Label $isActive={index === activeTabIndex}>{tab.name}</Label>}
                </AvatarContainer>
                <BioContainer>
                    <Position>{tab.position}</Position>
                    <Organisation>{tab.organisation}</Organisation>
                </BioContainer>
            </Tab>
        );
    };

    return (
        <>
            <VisuallyHidden>
                <a href={`#solent_story_tab_navigation_skip_${id}`}>
                    Skip solent story tab navigation / carousel
                </a>
            </VisuallyHidden>
            <PanelWrapper>
                {animation((style, item) => (
                    <Panel
                        id={`panel-${item.id}`}
                        key={`panel-${item.id}`}
                        role="tabpanel"
                        tabIndex={0}
                        aria-labelledby={`tab-${item.id}`}
                        style={style}
                    >
                        {item.content}
                    </Panel>
                ))}
            </PanelWrapper>
            {isMediumBreakpoint && tabs.length > 1 && (
                <Tabs role="tablist" aria-label="Graduate stories tabs" ref={tabsRef}>
                    {tabs.map((tab, index) => renderTab(tab, index))}
                </Tabs>
            )}
            {!isMediumBreakpoint && tabs.length > 1 && (
                <AvatarMobile tabs={tabs} activeTab={index => setActiveTabIndex(index)} />
            )}

            <span id={`solent_story_tab_navigation_skip_${id}`} />
        </>
    );
};

export default Avatar;
